import { reverse } from 'lodash';
import { IObservableArray, runInAction, toJS } from 'mobx';
import { flow, getSnapshot, getParent, types } from 'mobx-state-tree';

import { withEnvironment } from '../extensions/with-environment';
import {
    RunModel,
    RunModelModel,
    RunModelSnapshotIn,
} from '../RunModel/RunModel';
import { logger } from 'services/Logger';

export const RecordListModel = types
    .model({
        tempRuns: types.array(RunModelModel),
        tricks: types.array(RunModelModel),
        friendsRuns: types.array(RunModelModel),
        serverRuns: types.array(RunModelModel),
        publicRuns: types.array(RunModelModel),
    })
    .extend(withEnvironment)
    .views((self) => ({
        get lastTempRun() {
            if (self.tempRuns.length === 0) return undefined;
            return self.tempRuns[self.tempRuns.length - 1];
        },
        get favorites() {
            return self.tricks.filter((trick) => trick.favorite);
        },
        get friendFavorites() {
            return self.friendsRuns.filter((trick) => trick.favorite);
        },
        get publicFavorites() {
            return self.publicRuns.filter((trick) => trick.favorite);
        },
    }))
    .volatile((_self) => ({
        numRuns: 0,
    }))
    .actions((self) => {
        const recalcNumRuns = () => {
            self.numRuns = self.tricks.length + self.tempRuns.length;
        };
        const pushRuns = (tricks: Array<RunModel | RunModelSnapshotIn>) => {
            const newRuns = tricks.map((t) =>
                RunModelModel.create(toJS(t), self.environment)
            );
            self.tricks.push(...newRuns);
            recalcNumRuns();
            return newRuns;
        };

        const pushRun = (trick: RunModel | RunModelSnapshotIn) => {
            return pushRuns([trick])[0];
        };

        const addRunsTo = (tricks: Array<RunModel>, target: IObservableArray) => {
            // @ts-ignore
            const newRecords: Array<RunModel> = tricks
                .map((trick) => {
                    const newRecord = RunModelModel.create(trick, self.environment);
                    if (target.find((el) => el.id === newRecord.id)) return null;
                    return newRecord;
                })
                .filter((r) => r !== null);
            target.push(...newRecords);
            return newRecords;
        };
        const addFriendRuns = (tricks: Array<RunModel>) =>
            addRunsTo(tricks, self.friendsRuns);

        const addServerRuns = (tricks: Array<RunModel>) =>
            addRunsTo(tricks, self.serverRuns);

        const addPublicRuns = (tricks: Array<RunModel>) =>
            addRunsTo(tricks, self.publicRuns);

        const deleteRun = (item: RunModel) => {
            // TODO trigger a cloud function that deletes the json and video etc
            let idx = -1;
            const id = item.shareUrl;
            if (item.favorite) item.toggleFavorite();

            // Verwende runInAction, um sicherzustellen, dass Änderungen sofort beobachtbar sind
            return runInAction(() => {
                if ((idx = self.tricks.findIndex((v) => v.id === item.id)) !== -1) {
                    if (id) self.environment.cloudStorage.delRun(id); // async, aber wir warten nicht
                    self.tricks.splice(idx, 1); // Explizit 1 als zweiten Parameter angeben
                    recalcNumRuns();
                    return true;
                } else {
                    // Versuche, das Element aus jedem der Arrays zu entfernen
                    const wasInTemp = self.tempRuns.remove(item);
                    const wasInFriends = self.friendsRuns.remove(item);
                    const wasInServer = self.serverRuns.remove(item);
                    const wasInPublic = self.publicRuns.remove(item);

                    // Mindestens eines der Arrays sollte eine Änderung melden
                    const ret = wasInTemp || wasInFriends || wasInServer || wasInPublic;
                    recalcNumRuns();
                    return ret;
                }
            });
        };

        return {
            addFriendRuns,
            addServerRuns,
            addPublicRuns,
            pushRun,
            pushRuns,
            deleteRun,

            init(tricks: RunModel[]) {
                self.tricks.clear();
                tricks.forEach((trick) =>
                    self.tricks.push(RunModelModel.create(trick, self.environment))
                );
                recalcNumRuns();
            },
            findRun(trickId: string) {
                const pred = (trick: RunModel) => trick.id === trickId;
                return (
                    self.tricks.find(pred) ||
                    self.friendsRuns.find(pred) ||
                    self.serverRuns.find(pred) ||
                    self.publicRuns.find(pred) // Include public tricks in search
                );
            },
            addTemp(trick: RunModel) {
                const newRecord = RunModelModel.create(trick, self.environment);
                self.tempRuns.push(newRecord);
                recalcNumRuns();
            },
            deleteAll() {
                self.tricks.clear();
                self.friendsRuns.clear();
                self.tempRuns.clear();
                self.publicRuns.clear(); // Clear public tricks too
                recalcNumRuns();
            },
            deleteAllFriendRuns() {
                self.friendsRuns.clear();
                recalcNumRuns();
            },
            deleteAllServerRuns() {
                self.serverRuns.clear();
                recalcNumRuns();
            },
            deleteAllPublicRuns() {
                self.publicRuns.clear();
                recalcNumRuns();
            },
            deleteUnanalyzedRuns() {
                self.tempRuns.clear();
                recalcNumRuns();
            },


            // When refreshing our public runs list, we only want
            // newer data than the latest trick in local cache.
            // We also only hold $limit tricks at max to reduce traffic and 
            // database load
            refreshPublicRuns: flow(function*() {
                const limit = 10;
                let newestLocalTrick = self.publicRuns.at(0);
                let runs = yield self.environment.cloudStorage.getNewerPublicRuns(newestLocalTrick?.date);

                for (let run of reverse(runs)) {
                    self.publicRuns.unshift(RunModelModel.create(run, self.environment))
                }
                const overflow = self.publicRuns.length - limit;
                if (overflow > 0) {
                    self.publicRuns.splice(-overflow, overflow);
                }
            }),

            // When the user has scrolled through all public runs in the cache,
            // we might want to load more (older) data. To reduce traffic we only load
            // the X entries older than the oldest run in local cache.
            loadMorePublicRuns: flow(function*() {
                let oldestLocalTrick = self.publicRuns.at(self.publicRuns.length - 1);
                let runs = yield self.environment.cloudStorage.getOlderPublicRuns(oldestLocalTrick?.date);

                for (let run of runs) {
                    self.publicRuns.push(RunModelModel.create(run, self.environment))
                }
                // In this case we don't want to truncate the array
            }),

            addNewRun: flow(function*(trick: RunModel) {
                const trickCpy = getSnapshot(trick);
                const deleteResult = deleteRun(trick);
                const newRecord = runInAction(() => {
                    const newRec = pushRun(trickCpy);
                    return newRec;
                });

                try {
                    if (
                        newRecord.afterCreate &&
                        typeof newRecord.afterCreate === 'function'
                    ) {
                        const afterCreateResult = newRecord.afterCreate();
                        // Nur yielden wenn es ein Promise ist
                        if (
                            afterCreateResult &&
                            typeof afterCreateResult.then === 'function'
                        ) {
                            yield afterCreateResult;
                        }
                    }

                    // ✅ Video upload (async)
                    if (newRecord.localVideoUri !== null) {
                        const videoUrl = yield self.environment.cloudStorage.uploadVideo(
                            newRecord.deviceId,
                            newRecord.recordId,
                            true
                        );
                        newRecord.setVideoURL(videoUrl);
                    }
                    yield self.environment.cloudStorage.addRun(newRecord);

                    try {
                        const rootStore = getParent(self) as any;
                        if (rootStore && rootStore.currentUser) {
                            const recalcResult = rootStore.currentUser.recalculateScore();
                            if (recalcResult && typeof recalcResult.then === 'function') {
                                yield recalcResult;
                            } 
                            const allScores = self.tricks.map((t) => t.totalScore || 0);
                            const maxScore =
                                allScores.length > 0 ? Math.max(...allScores) : 0;
                            if (rootStore.currentUser.score !== maxScore && maxScore > 0) {
                                yield rootStore.currentUser.updateScore(maxScore);
                            }
                        }
                    } catch (error) {
                        logger.error('Score recalculation failed:', error);
                    }

                    runInAction(() => {
                        recalcNumRuns();
                    });

                    return newRecord;
                } catch (error) {
                    logger.error('AddNewRun failed:', {
                        error: error,
                        errorMessage:
                            error instanceof Error ? error.message : String(error),
                        trickId: trick.id,
                    });

                    // Bei Fehler: Rollback (synchron)
                    try {
                        deleteRun(newRecord);
                    } catch (rollbackError) {
                        logger.error('Rollback failed:', rollbackError);
                    }
                    throw error;
                }
            }),
            addFriendRun(trick: RunModel) {
                return addFriendRuns([trick])[0];
            },
            downloadMissingData() {
                [...self.tricks, ...self.friendsRuns, ...self.publicRuns].forEach(
                    (trick) => {
                        if (!trick.playData) {
                            trick.downloadPlayData();
                            trick.downloadVideo();
                        }
                    }
                );
            },
        };
    });
